import { useEffect, useState, type FormEvent } from 'react';
import { useOp } from '../../api.ts';
import { useRouter } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { step, useKeys } from '../../ui/keys.ts';
import { Drawer, Empty, ErrorBox, Kbd, Loading, age } from '../../ui/kit.tsx';
import './sources.css';
import { useToast } from '../../ui/toasts.tsx';
import type { CompetitorSignal, CompetitorSource, ExclusivityToken, RunCoupon, SourceCandidate, SourceRun, TrackedPage } from './types.ts';

const TABS = [
  { key: 'competitors', label: 'Competitors' },
  { key: 'candidates', label: 'Found codes' },
  { key: 'signals', label: 'Signals' },
  { key: 'tokens', label: 'Exclusivity tokens' },
] as const;
type Tab = (typeof TABS)[number]['key'];

const VERDICT_TONE: Record<string, string> = { ok: 'badge-ok', review: 'badge-warn', skip: '' };
const VERDICT_LABEL: Record<string, string> = { ok: 'Usable', review: 'Review', skip: 'Skipped' };
const STATUS_TONE: Record<string, string> = { success: 'badge-ok', partial: 'badge-warn', blocked: 'badge-warn', failed: 'badge-warn', running: '' };

/**
 * Competitor sources (super admin): what each tracked market leader returned, what it cost, which codes were skipped as
 * exclusive and why, and the token list that decides it. Nothing here publishes a coupon.
 */
export function CompetitorSources() {
  const { query, setQuery } = useRouter();
  const tab = (TABS.find((t) => t.key === query.get('tab'))?.key ?? 'competitors') as Tab;
  useKeys({
    '1': () => setQuery({ tab: 'competitors' }, { replace: true }),
    '2': () => setQuery({ tab: 'candidates' }, { replace: true }),
    '3': () => setQuery({ tab: 'signals' }, { replace: true }),
    '4': () => setQuery({ tab: 'tokens' }, { replace: true }),
  });
  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Competitor sources</h1>
          <p className="muted">
            Codes found on one market leader per country. They become candidates only: exclusive or competitor-branded codes are skipped, borderline ones wait for review, and nothing goes live from here. We fetch store pages and code
            pop-ups only, never a competitor's clickout.
          </p>
        </div>
      </header>
      <div className="segmented tabs" role="tablist" aria-label="Sections">
        {TABS.map((t, i) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} aria-pressed={tab === t.key} onClick={() => setQuery({ tab: t.key }, { replace: true })}>
            {t.label} <Kbd>{i + 1}</Kbd>
          </button>
        ))}
      </div>
      {tab === 'competitors' ? <Competitors /> : null}
      {tab === 'candidates' ? <Candidates /> : null}
      {tab === 'signals' ? <Signals /> : null}
      {tab === 'tokens' ? <Tokens /> : null}
    </section>
  );
}

function credits(n: number): string {
  return `${n.toLocaleString('en-GB')} credit${n === 1 ? '' : 's'}`;
}

function Competitors() {
  const changes = useChanges();
  const overview = useOp<{ competitors: CompetitorSource[] }>('admin.sources.competitors', {}, changes);
  const [cursor, setCursor] = useState(0);
  const [open, setOpen] = useState<CompetitorSource | null>(null);
  const rows = overview.data?.competitors ?? [];
  useKeys(
    {
      j: () => setCursor((c) => step(c, 1, rows.length)),
      k: () => setCursor((c) => step(c, -1, rows.length)),
      o: () => rows[cursor] && setOpen(rows[cursor]!),
      enter: () => rows[cursor] && setOpen(rows[cursor]!),
    },
    open === null,
  );
  if (overview.error) return <ErrorBox error={overview.error} onRetry={overview.reload} />;
  if (!overview.data) return <Loading />;
  return (
    <>
      <table className="table compact" aria-label="Competitors">
        <thead>
          <tr>
            <th scope="col">Competitor</th>
            <th scope="col">Market</th>
            <th scope="col">Fetch</th>
            <th scope="col">Pages</th>
            <th scope="col">Last run</th>
            <th scope="col">Found (usable / review / skipped)</th>
            <th scope="col">Cost, 30 days</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c, index) => (
            <tr key={c.slug} className={`clickable ${index === cursor ? 'cursor' : ''}`} onClick={() => setOpen(c)} onMouseEnter={() => setCursor(index)}>
              <td>
                <strong>{c.name}</strong>
                <div className="small muted">{c.site}</div>
              </td>
              <td>{c.countryCode}</td>
              <td className="small">{c.pageTiers.join(' → ')}</td>
              <td>
                {c.pagesEnabled}
                {c.pages !== c.pagesEnabled ? <span className="muted small"> of {c.pages}</span> : null}
              </td>
              <td className="small">
                {c.lastRun ? (
                  <>
                    <span className={`badge ${STATUS_TONE[c.lastRun.status] ?? ''}`}>{c.lastRun.status}</span> {age(c.lastRun.startedAt)}
                  </>
                ) : (
                  <span className="muted">never</span>
                )}
              </td>
              <td>
                {c.candidates.ok} / {c.candidates.review} / {c.candidates.skip}
              </td>
              <td className="small">
                {credits(c.credits30d)} · {c.runs30d} run{c.runs30d === 1 ? '' : 's'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted small">Runs come from the scheduled job or the command line (pnpm --dir packages/sourcing competitors:run). Direct requests cost nothing; ScraperAPI tiers cost credits.</p>
      {open ? <CompetitorDrawer competitor={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function CompetitorDrawer({ competitor, onClose }: { competitor: CompetitorSource; onClose: () => void }) {
  const toast = useToast();
  const changes = useChanges();
  const runs = useOp<{ runs: SourceRun[] }>('admin.sources.runs', { competitor: competitor.slug, limit: 10 }, changes);
  const pages = useOp<{ rows: TrackedPage[] }>('admin.sources.pages', { competitor: competitor.slug }, changes);
  const [url, setUrl] = useState('');
  const [shopDomain, setShopDomain] = useState('');
  const [openRun, setOpenRun] = useState<SourceRun | null>(null);
  // A run in progress updates its counters as it goes: refresh every 15 seconds while one is running.
  const running = runs.data?.runs.some((r) => r.status === 'running') ?? false;
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(runs.reload, 15_000);
    return () => clearInterval(timer);
  }, [running, runs.reload]);

  const add = async (event: FormEvent) => {
    event.preventDefault();
    const result = await runWrite<{ created: boolean; url: string }>(toast, 'admin.sources.pages.save', { competitor: competitor.slug, url: url.trim(), ...(shopDomain.trim() ? { shopDomain: shopDomain.trim() } : {}) }, (r) =>
      r.created ? `Tracking ${r.url}` : `${r.url} was already tracked`,
    );
    if (result) {
      setUrl('');
      setShopDomain('');
    }
  };

  return (
    <Drawer title={competitor.name} onClose={onClose} wide>
      <dl className="facts">
        <dt>Market</dt>
        <dd>{competitor.countryCode}</dd>
        <dt>Store pages</dt>
        <dd>{competitor.pageTiers.join(' → ')}</dd>
        <dt>Codes</dt>
        <dd>
          {competitor.codeRetrieval}
          {competitor.codeTiers.length ? ` (${competitor.codeTiers.join(' → ')})` : ''}
        </dd>
      </dl>

      <h3>Recent runs</h3>
      {runs.error ? <ErrorBox error={runs.error} onRetry={runs.reload} /> : null}
      {runs.data && runs.data.runs.length === 0 ? <Empty>No runs yet.</Empty> : null}
      {runs.data?.runs.length ? (
        <table className="table compact" aria-label="Runs">
          <thead>
            <tr>
              <th scope="col">Started</th>
              <th scope="col">Status</th>
              <th scope="col">Pages</th>
              <th scope="col">Offers / codes</th>
              <th scope="col">New · skipped · review</th>
              <th scope="col">Coupons</th>
              <th scope="col">Requests · cost</th>
            </tr>
          </thead>
          <tbody>
            {runs.data.runs.map((r) => (
              <tr key={r.ref} className="clickable" tabIndex={0} onClick={() => setOpenRun(r)} onKeyDown={(e) => (e.key === 'Enter' ? setOpenRun(r) : undefined)} title={[...r.errors.map((e) => `${e.url}: ${e.message}`), ...r.warnings].join('\n') || 'Open the run'}>
                <td className="small nowrap">{new Date(r.startedAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}</td>
                <td>
                  <span className={`badge ${STATUS_TONE[r.status] ?? ''}`}>{r.status}</span>
                </td>
                <td>
                  {r.pagesOk}/{r.pagesTotal}
                </td>
                <td>
                  {r.offersFound} / {r.codesFound}
                </td>
                <td>
                  {r.candidatesNew} · {r.skippedExclusive} · {r.sentToReview}
                </td>
                <td className="small nowrap">
                  {r.coupons ? (
                    <>
                      {r.coupons.created} new · {r.coupons.updated} upd.
                      <div className="muted">
                        {r.coupons.live} live · {r.coupons.review} review
                      </div>
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="small">
                  {r.requestsMade} · {credits(r.credits)}
                  <div className="muted">{Object.entries(r.tierUsage).map(([tier, u]) => `${tier}: ${u.succeeded}/${u.requests}`).join(', ')}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {runs.data?.runs[0]?.errors.length ? (
        <div className="error-box small">
          {runs.data.runs[0].errors.slice(0, 5).map((e) => (
            <div key={e.url}>
              {e.url}: {e.message}
            </div>
          ))}
        </div>
      ) : null}

      {openRun ? <RunDrawer run={openRun} onClose={() => setOpenRun(null)} /> : null}

      <h3>Tracked store pages</h3>
      {competitor.autoTracked ? <p className="muted small">Every store our active sites carry in {competitor.countryCode} is tracked automatically before each run; pause a page here to skip it.</p> : null}
      <form className="inline-form" onSubmit={add}>
        <label className="sr-only" htmlFor="source-page-url">
          Store page URL
        </label>
        <input id="source-page-url" type="url" required placeholder={`https://www.${competitor.site}/…`} value={url} onChange={(e) => setUrl(e.target.value)} />
        <label className="sr-only" htmlFor="source-page-domain">
          Merchant domain
        </label>
        <input id="source-page-domain" placeholder="merchant domain (if the page does not name it)" value={shopDomain} onChange={(e) => setShopDomain(e.target.value)} />
        <button className="btn btn-primary btn-small" type="submit">
          Track page
        </button>
      </form>
      {pages.data && pages.data.rows.length === 0 ? <Empty>No pages tracked yet.</Empty> : null}
      {pages.data?.rows.length ? (
        <table className="table compact" aria-label="Tracked pages">
          <tbody>
            {pages.data.rows.map((p) => (
              <tr key={p.url}>
                <td className="small truncate">
                  {p.url}
                  {p.shopDomain ? <span className="muted"> · {p.shopDomain}</span> : null}
                </td>
                <td className="small nowrap">{p.lastScrapedAt ? `${p.lastStatus ?? ''} ${age(p.lastScrapedAt)}` : 'not run yet'}</td>
                <td>
                  <label className="inline-check">
                    <input
                      type="checkbox"
                      checked={p.enabled}
                      onChange={(e) => void runWrite(toast, 'admin.sources.pages.save', { competitor: competitor.slug, url: p.url, enabled: e.target.checked }, e.target.checked ? 'Page tracked again' : 'Page paused')}
                    />
                    On
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </Drawer>
  );
}

const OUTCOME_LABEL: Record<string, string> = { created: 'New coupon', updated: 'Updated', duplicate: 'Already listed', needs_merchant: 'No store', invalid: 'Not usable' };
const STATE_TONE: Record<string, string> = { approved: 'badge-ok', pending_review: 'badge-warn' };
const STATE_LABEL: Record<string, string> = { approved: 'live', pending_review: 'review', paused: 'paused', rejected: 'rejected' };

/** One run: every find it sent to intake, and the coupon each one created or updated with its state per site. */
function RunDrawer({ run, onClose }: { run: SourceRun; onClose: () => void }) {
  const { navigate } = useRouter();
  const [outcome, setOutcome] = useState<'all' | 'created' | 'updated' | 'none'>('all');
  const detail = useOp<{ run: SourceRun; rows: RunCoupon[] }>('admin.sources.run', { ref: run.ref, outcome });
  const current = detail.data?.run ?? run;
  useEffect(() => {
    if (current.status !== 'running') return;
    const timer = setInterval(detail.reload, 15_000);
    return () => clearInterval(timer);
  }, [current.status, detail.reload]);
  const c = current.coupons;
  return (
    <Drawer title={`Run ${new Date(current.startedAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}`} onClose={onClose} wide>
      <dl className="facts">
        <dt>Status</dt>
        <dd>
          <span className={`badge ${STATUS_TONE[current.status] ?? ''}`}>{current.status}</span> · {current.triggeredBy}
          {current.finishedAt ? ` · ${Math.round((new Date(current.finishedAt).getTime() - new Date(current.startedAt).getTime()) / 60_000)} min` : ' · in progress'}
        </dd>
        <dt>Pages</dt>
        <dd>
          {current.pagesOk}/{current.pagesTotal} ok{current.pagesFailed ? `, ${current.pagesFailed} failed` : ''}
        </dd>
        <dt>Found</dt>
        <dd>
          {current.offersFound} offers, {current.codesFound} codes · {current.skippedExclusive} skipped as exclusive
        </dd>
        <dt>Coupons</dt>
        <dd>{c ? `${c.created} new, ${c.updated} updated · ${c.live} live, ${c.review} waiting for review · ${c.none} without a coupon` : '—'}</dd>
        <dt>Cost</dt>
        <dd>
          {current.requestsMade} requests · {credits(current.credits)}
        </dd>
      </dl>
      {current.errors.length ? (
        <details className="error-box small">
          <summary>{current.errors.length} page error(s)</summary>
          {current.errors.slice(0, 50).map((e) => (
            <div key={e.url}>
              {e.url}: {e.message}
            </div>
          ))}
        </details>
      ) : null}

      <div className="segmented" role="group" aria-label="Outcome">
        {(['all', 'created', 'updated', 'none'] as const).map((o) => (
          <button key={o} type="button" aria-pressed={outcome === o} onClick={() => setOutcome(o)}>
            {o === 'all' ? 'All' : o === 'created' ? 'New coupons' : o === 'updated' ? 'Updated' : 'No coupon'}
          </button>
        ))}
      </div>
      {detail.error ? <ErrorBox error={detail.error} onRetry={detail.reload} /> : null}
      {detail.loading && !detail.data ? <Loading /> : null}
      {detail.data && detail.data.rows.length === 0 ? <Empty>Nothing here for this run.</Empty> : null}
      {detail.data?.rows.length ? (
        <table className="table compact" aria-label="Run coupons">
          <thead>
            <tr>
              <th scope="col">Store</th>
              <th scope="col">Find</th>
              <th scope="col">Result</th>
              <th scope="col">On our sites</th>
            </tr>
          </thead>
          <tbody>
            {detail.data.rows.map((r) => (
              <tr key={r.candidateRef}>
                <td className="small">{r.shopName ?? r.shopDomain}</td>
                <td className="small">
                  {r.code ? <code>{r.code}</code> : <span className="muted">{r.kind}</span>} {r.title}
                  {r.labels.some((l) => l.label === 'status' && /verified/i.test(l.value)) ? <span className="badge badge-ok">verified</span> : null}
                </td>
                <td className="small">
                  {r.coupon ? (
                    <button type="button" className="link-button" onClick={() => navigate(`/coupons?coupon=${r.coupon!.ref}`)}>
                      {OUTCOME_LABEL[r.outcome] ?? r.outcome}
                    </button>
                  ) : (
                    <span title={r.message}>{OUTCOME_LABEL[r.outcome] ?? r.outcome}</span>
                  )}
                  {!r.coupon ? <div className="muted">{r.message}</div> : null}
                </td>
                <td className="small">
                  {r.coupon?.sites.length
                    ? r.coupon.sites.map((s) => (
                        <span key={s.site} className={`badge ${s.hidden ? '' : (STATE_TONE[s.state] ?? '')}`} title={s.site}>
                          {s.site}: {s.hidden ? 'hidden' : (STATE_LABEL[s.state] ?? s.state)}
                        </span>
                      ))
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </Drawer>
  );
}

function Candidates() {
  const { query, setQuery } = useRouter();
  const verdict = (query.get('verdict') ?? 'all') as 'all' | 'ok' | 'review' | 'skip';
  const competitor = query.get('competitor') ?? '';
  const q = query.get('q') ?? '';
  const list = useOp<{ rows: SourceCandidate[] }>('admin.sources.candidates', { verdict, ...(competitor ? { competitor } : {}), ...(q ? { q } : {}), limit: 200 });
  const competitors = useOp<{ competitors: CompetitorSource[] }>('admin.sources.competitors', {});
  const [cursor, setCursor] = useState(0);
  const [open, setOpen] = useState<SourceCandidate | null>(null);
  const rows = list.data?.rows ?? [];
  useEffect(() => setCursor(0), [verdict, competitor, q]);
  useKeys(
    {
      j: () => setCursor((c) => step(c, 1, rows.length)),
      k: () => setCursor((c) => step(c, -1, rows.length)),
      o: () => rows[cursor] && setOpen(rows[cursor]!),
      enter: () => rows[cursor] && setOpen(rows[cursor]!),
    },
    open === null,
  );
  return (
    <>
      <div className="toolbar">
        <label className="inline-field">
          Competitor
          <select value={competitor} onChange={(e) => setQuery({ competitor: e.target.value || null }, { replace: true })}>
            <option value="">All</option>
            {competitors.data?.competitors.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name} ({c.countryCode})
              </option>
            ))}
          </select>
        </label>
        <label className="inline-field">
          Verdict
          <select value={verdict} onChange={(e) => setQuery({ verdict: e.target.value === 'all' ? null : e.target.value }, { replace: true })}>
            <option value="all">All</option>
            <option value="ok">Usable</option>
            <option value="review">Review</option>
            <option value="skip">Skipped as exclusive</option>
          </select>
        </label>
        <label className="inline-field">
          Search
          <input defaultValue={q} placeholder="merchant, code or title" onKeyDown={(e) => e.key === 'Enter' && setQuery({ q: e.currentTarget.value || null }, { replace: true })} />
        </label>
      </div>
      {list.error ? <ErrorBox error={list.error} onRetry={list.reload} /> : null}
      {!list.data && list.loading ? <Loading /> : null}
      {list.data && rows.length === 0 ? <Empty>Nothing found yet.</Empty> : null}
      {rows.length ? (
        <table className="table compact" aria-label="Found codes">
          <thead>
            <tr>
              <th scope="col">Merchant</th>
              <th scope="col">Code</th>
              <th scope="col">Offer</th>
              <th scope="col">Verdict</th>
              <th scope="col">Why</th>
              <th scope="col">Seen</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, index) => (
              <tr key={r.ref} className={`clickable ${index === cursor ? 'cursor' : ''}`} onClick={() => setOpen(r)} onMouseEnter={() => setCursor(index)}>
                <td>
                  {r.shopName ?? r.shopDomain}
                  <div className="small muted">
                    {r.competitor} · {r.countryCode}
                    {r.merchantMatched ? '' : ' · not in our catalog'}
                  </div>
                </td>
                <td>{r.code && !r.codeNotRequired ? <code>{r.code}</code> : <span className="muted small">{r.kind === 'deal' ? 'deal' : (r.code ?? 'no code')}</span>}</td>
                <td className="small truncate">{r.title}</td>
                <td>
                  <span className={`badge ${VERDICT_TONE[r.verdict] ?? ''}`}>{VERDICT_LABEL[r.verdict] ?? r.verdict}</span>
                </td>
                <td className="small truncate">{r.reasons.filter((x) => x.weight > 0 || x.hard).map((x) => x.detail).join('; ') || '—'}</td>
                <td className="small nowrap">
                  {age(r.lastSeenAt)} · {r.seenCount}×
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {open ? <CandidateDrawer candidate={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function CandidateDrawer({ candidate: c, onClose }: { candidate: SourceCandidate; onClose: () => void }) {
  return (
    <Drawer title={`${c.shopName ?? c.shopDomain}: ${c.title}`} onClose={onClose} wide>
      <p>
        <span className={`badge ${VERDICT_TONE[c.verdict] ?? ''}`}>{VERDICT_LABEL[c.verdict] ?? c.verdict}</span> score {c.score.toFixed(2)} · {c.sourcingState}
      </p>
      <h3>Why</h3>
      {c.reasons.length === 0 ? <p className="muted">No exclusivity signals: an ordinary public code.</p> : null}
      <ul>
        {c.reasons.map((r) => (
          <li key={r.code}>
            {r.detail} <span className="muted small">({r.hard ? 'decides alone' : `${r.weight > 0 ? '+' : ''}${r.weight}`})</span>
          </li>
        ))}
      </ul>
      <h3>As the competitor shows it</h3>
      <dl className="facts">
        <dt>Code</dt>
        <dd>{c.code ?? '—'}</dd>
        <dt>Savings</dt>
        <dd>{c.savings ?? '—'}</dd>
        <dt>Expires</dt>
        <dd>{c.endsOn ?? c.expirationText ?? '—'}</dd>
        <dt>Labels</dt>
        <dd>{c.labels.map((l) => `${l.label}: ${l.value}`).join(', ') || '—'}</dd>
      </dl>
      <h3>Provenance</h3>
      <dl className="facts">
        <dt>Source</dt>
        <dd>
          {c.competitor} · {c.sourceUrl}
        </dd>
        <dt>Fetched</dt>
        <dd>
          {new Date(c.fetchedAt).toLocaleString('en-GB')} via {c.fetchTier ?? '—'}
        </dd>
        <dt>First seen</dt>
        <dd>
          {new Date(c.firstSeenAt).toLocaleString('en-GB')} · seen {c.seenCount}×
        </dd>
      </dl>
    </Drawer>
  );
}

function Signals() {
  const { query, setQuery } = useRouter();
  const country = query.get('country') ?? '';
  const signals = useOp<{ rows: CompetitorSignal[] }>('admin.sources.signals', country ? { countryCode: country } : {});
  return (
    <>
      <div className="toolbar">
        <label className="inline-field">
          Market
          <input defaultValue={country} placeholder="e.g. GB" maxLength={2} onKeyDown={(e) => e.key === 'Enter' && setQuery({ country: e.currentTarget.value.toUpperCase() || null }, { replace: true })} />
        </label>
        <span className="muted small">Codes seen in the last 14 days. Signals only: they feed the Opportunities lane and the &ldquo;ask for our own code&rdquo; finding.</span>
      </div>
      {signals.error ? <ErrorBox error={signals.error} onRetry={signals.reload} /> : null}
      {!signals.data ? <Loading /> : null}
      {signals.data && signals.data.rows.length === 0 ? <Empty>No competitor data yet.</Empty> : null}
      {signals.data?.rows.length ? (
        <table className="table compact" aria-label="Competitor signals">
          <thead>
            <tr>
              <th scope="col">Merchant</th>
              <th scope="col">Competitor codes</th>
              <th scope="col">We lack</th>
              <th scope="col">Exclusive to them</th>
              <th scope="col">Next step</th>
            </tr>
          </thead>
          <tbody>
            {signals.data.rows.map((s) => (
              <tr key={`${s.countryCode}:${s.shopDomain}`}>
                <td>
                  {s.merchantName ?? s.shopName ?? s.shopDomain}
                  <div className="small muted">
                    {s.countryCode} · {s.competitors.join(', ')}
                  </div>
                </td>
                <td>{s.competitorCodes}</td>
                <td>
                  {s.codesWeLack}
                  {s.bestMissingSavings ? <div className="small muted">{s.bestMissingSavings}</div> : null}
                </td>
                <td>{s.exclusiveCodes}</td>
                <td className="small">{s.finding ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </>
  );
}

const EMPTY_TOKEN = { token: '', owner: '', kind: 'competitor', strength: 'hard', countryCode: '' };

function Tokens() {
  const toast = useToast();
  const changes = useChanges();
  const list = useOp<{ rows: ExclusivityToken[]; kinds: string[]; strengths: string[] }>('admin.sources.tokens', {}, changes);
  const [draft, setDraft] = useState(EMPTY_TOKEN);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    const result = await runWrite<{ token: string }>(toast, 'admin.sources.tokens.save', { ...draft, countryCode: draft.countryCode.trim() ? draft.countryCode.trim().toUpperCase() : null }, (r) => `Saved ${r.token}`);
    if (result) setDraft(EMPTY_TOKEN);
  };
  const update = (t: ExclusivityToken, change: Partial<ExclusivityToken>) =>
    runWrite(toast, 'admin.sources.tokens.save', { id: t.id, token: t.token, owner: t.owner, kind: t.kind, strength: t.strength, countryCode: t.countryCode, enabled: t.enabled, notes: t.notes, ...change }, `Saved ${t.token}`);

  if (list.error) return <ErrorBox error={list.error} onRetry={list.reload} />;
  if (!list.data) return <Loading />;
  return (
    <>
      <p className="muted small">
        A code containing a token was issued to someone else (RTMN10, HUKD15). <strong>Hard</strong> tokens skip the code; <strong>soft</strong> tokens and look-alikes send it to review. Short tokens only match as a whole letter group or next to a
        common word (VCSAVE), so VC never matches inside VCARD.
      </p>
      <form className="inline-form" onSubmit={save}>
        <input aria-label="Token" required placeholder="Token, e.g. HUKD" value={draft.token} onChange={(e) => setDraft({ ...draft, token: e.target.value })} />
        <input aria-label="Owner" required placeholder="Belongs to, e.g. hotukdeals" value={draft.owner} onChange={(e) => setDraft({ ...draft, owner: e.target.value })} />
        <select aria-label="Kind" value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
          {list.data.kinds.map((k) => (
            <option key={k}>{k}</option>
          ))}
        </select>
        <select aria-label="Strength" value={draft.strength} onChange={(e) => setDraft({ ...draft, strength: e.target.value })}>
          {list.data.strengths.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <input aria-label="Market" className="input-narrow" placeholder="Market (empty = all)" maxLength={2} value={draft.countryCode} onChange={(e) => setDraft({ ...draft, countryCode: e.target.value })} />
        <button className="btn btn-primary btn-small" type="submit">
          Add token
        </button>
      </form>
      <table className="table compact" aria-label="Exclusivity tokens">
        <thead>
          <tr>
            <th scope="col">Token</th>
            <th scope="col">Belongs to</th>
            <th scope="col">Kind</th>
            <th scope="col">Strength</th>
            <th scope="col">Market</th>
            <th scope="col">On</th>
            <th scope="col">Changed</th>
          </tr>
        </thead>
        <tbody>
          {list.data.rows.map((t) => (
            <tr key={t.id} className={t.enabled ? '' : 'muted'}>
              <td>
                <code>{t.token}</code>
              </td>
              <td>{t.owner}</td>
              <td className="small">{t.kind}</td>
              <td>
                <select aria-label={`Strength of ${t.token}`} value={t.strength} onChange={(e) => void update(t, { strength: e.target.value as 'hard' | 'soft' })}>
                  {list.data!.strengths.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </td>
              <td>{t.countryCode ?? 'all'}</td>
              <td>
                <input type="checkbox" aria-label={`${t.token} on`} checked={t.enabled} onChange={(e) => void update(t, { enabled: e.target.checked })} />
              </td>
              <td className="small muted">
                {t.updatedBy ?? '—'} · {age(t.updatedAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
